import Link from 'next/link';
import Image from 'next/image';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useRouter } from 'next/router';
import Modal from '@/components/ui/Modal';
import ForumAuthModal from '@/components/forum/ForumAuthModal';
import SiteLayout from '@/components/site/SiteLayout';
import ForumAvatar from '@/components/forum/ForumAvatar';
import { FORUM_SESSION_EXPIRED_EVENT } from '@/lib/forumSession';
import {
  cacheForumPosts,
  createForumPost,
  fetchForumPostsServer,
  fetchUserForumPosts,
  getForumSession,
  getForumSessionServerSnapshot,
  subscribeForumSession,
} from '@/lib/forumClient';
import { ForumPost } from '@/lib/forumData';

type ForumPageProps = {
  initialPosts: ForumPost[];
  initialError: string;
};

const POSTS_PER_PAGE = 20;
const POST_TITLE_MAX_LENGTH = 140;
const POST_BODY_MAX_LENGTH = 5000;

export const getServerSideProps: GetServerSideProps<ForumPageProps> =
  async () => {
    try {
      const initialPosts = await fetchForumPostsServer();
      return {
        props: {
          initialPosts,
          initialError: '',
        },
      };
    } catch (err: any) {
      return {
        props: {
          initialPosts: [],
          initialError: err?.message || 'Unable to load forum posts.',
        },
      };
    }
  };

const ForumPage = ({
  initialPosts,
  initialError,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const router = useRouter();
  const [posts, setPosts] = useState<ForumPost[]>(initialPosts || []);
  const [allPosts, setAllPosts] = useState<ForumPost[]>(initialPosts || []);
  const [loadingAction, setLoadingAction] = useState(false);
  const [loadingMyPosts, setLoadingMyPosts] = useState(false);
  const [pageError] = useState(initialError || '');
  const [formError, setFormError] = useState('');
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<'post' | 'my-posts' | null>(
    null,
  );
  const [view, setView] = useState<'all' | 'mine'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const storedSession = useSyncExternalStore(
    subscribeForumSession,
    getForumSession,
    getForumSessionServerSnapshot,
  );
  const session = storedSession;

  useEffect(() => {
    cacheForumPosts(initialPosts || []);
  }, [initialPosts]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleSessionExpired = () => {
      setAuthMode('login');
      setAuthOpen(true);
      setPostModalOpen(false);
      setFormError('Session expired. Please log in again.');
    };

    window.addEventListener(FORUM_SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(
        FORUM_SESSION_EXPIRED_EVENT,
        handleSessionExpired,
      );
    };
  }, []);

  useEffect(() => {
    if (!session?.id && view === 'mine') {
      const resetView = window.setTimeout(() => {
        setPosts(allPosts);
        setView('all');
        setCurrentPage(1);
      }, 0);

      return () => window.clearTimeout(resetView);
    }
  }, [allPosts, session?.id, view]);

  const totalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedPosts = useMemo(() => {
    const start = (safeCurrentPage - 1) * POSTS_PER_PAGE;
    return posts.slice(start, start + POSTS_PER_PAGE);
  }, [posts, safeCurrentPage]);

  const openCreatePost = () => {
    if (!session) {
      setPendingAction('post');
      setAuthMode('login');
      setAuthOpen(true);
      return;
    }

    setPostModalOpen(true);
  };

  const openMyPosts = () => {
    if (!session?.id) {
      setPendingAction('my-posts');
      setAuthMode('login');
      setAuthOpen(true);
      return;
    }

    setLoadingMyPosts(true);
    fetchUserForumPosts(String(session.id))
      .then((userPosts) => {
        setPosts(userPosts);
        setView('mine');
        setCurrentPage(1);
      })
      .catch((err: any) => {
        setFormError(err?.message || 'Unable to load your posts.');
      })
      .finally(() => {
        setLoadingMyPosts(false);
      });
  };

  const openAllPosts = () => {
    setPosts(allPosts);
    setView('all');
    setCurrentPage(1);
  };

  const handleAuthenticated = () => {
    setAuthOpen(false);

    if (pendingAction === 'post') {
      setPostModalOpen(true);
    }

    if (pendingAction === 'my-posts' && getForumSession()?.id) {
      setLoadingMyPosts(true);
      fetchUserForumPosts(String(getForumSession()?.id))
        .then((userPosts) => {
          setPosts(userPosts);
          setView('mine');
          setCurrentPage(1);
        })
        .catch((err: any) => {
          setFormError(err?.message || 'Unable to load your posts.');
        })
        .finally(() => {
          setLoadingMyPosts(false);
        });
    }

    setPendingAction(null);
  };

  const submitPost = () => {
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();

    if (!session || !trimmedTitle || !trimmedBody) return;

    if (
      trimmedTitle.length > POST_TITLE_MAX_LENGTH ||
      trimmedBody.length > POST_BODY_MAX_LENGTH
    ) {
      setFormError('Post is too long.');
      return;
    }

    setLoadingAction(true);
    setFormError('');

    createForumPost({
      title: trimmedTitle,
      body: trimmedBody,
      session,
    })
      .then((newPost) => {
        setAllPosts((current) => {
          const nextAllPosts = [newPost, ...current];
          cacheForumPosts(nextAllPosts);
          return nextAllPosts;
        });
        setPosts((current) => [newPost, ...current]);
        setCurrentPage(1);
        setTitle('');
        setBody('');
        setPostModalOpen(false);
        router.push(`/forum/${newPost.slug}`);
      })
      .catch((err: any) => {
        setFormError(err?.message || 'Unable to publish the post.');
      })
      .finally(() => {
        setLoadingAction(false);
      });
  };

  return (
    <SiteLayout
      title='DocMoonlight Forum | Physician Coverage Community'
      description='Browse DocMoonlight community discussions about physician coverage, imaging operations, resident moonlighting, and healthcare staffing topics.'
      keywords={[
        'DocMoonlight forum',
        'physician coverage community',
        'resident moonlighting discussion',
        'imaging operations',
        'medical staffing discussion',
      ]}
    >
      <section className='px-4 pb-14 pt-10 sm:px-6 lg:px-8 lg:pt-14'>
        <div className='mx-auto max-w-6xl'>
          <div className='relative overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_right,_rgba(245,158,11,0.12),_transparent_30%),radial-gradient(circle_at_top_left,_rgba(37,99,235,0.08),_transparent_36%),linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] px-5 py-8 shadow-[0_24px_80px_rgba(15,23,42,0.12)] sm:rounded-[2.5rem] sm:px-6 sm:py-10 md:px-10 md:py-12'>
            <div className='absolute -right-8 -top-6 h-72 w-72 rounded-full bg-[#fde68a]/45 blur-3xl' />
            <div className='absolute right-28 top-0 h-full w-[28rem] rounded-full bg-[radial-gradient(circle,_rgba(37,99,235,0.08)_0%,_transparent_62%)]' />

            <div className='relative'>
              <div className='flex flex-col gap-8 md:flex-row md:items-end md:justify-between'>
                <div className='max-w-3xl'>
                  <div className='inline-flex items-center gap-2 rounded-full border border-[#f5d6a2] bg-[#fff7eb] px-4 py-2 text-[0.68rem] font-black uppercase tracking-[0.12em] text-[#d18a00] shadow-[0_10px_30px_rgba(15,23,42,0.08)] sm:gap-3 sm:px-5 sm:py-3 sm:text-sm sm:tracking-[0.14em]'>
                    <svg
                      aria-hidden='true'
                      viewBox='0 0 24 24'
                      className='h-4 w-4 sm:h-5 sm:w-5'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
                      <path d='M8 9h8' />
                      <path d='M8 13h5' />
                    </svg>
                    Forum
                  </div>
                  <h1 className='mt-6 text-[1.85rem] font-black leading-tight tracking-[-0.04em] text-slate-950 sm:mt-8 sm:text-[2.3rem] md:text-[2.85rem] md:leading-[1]'>
                    Conversations for modern healthcare coverage.
                  </h1>
                  <p className='mt-4 max-w-2xl text-sm leading-6 text-slate-600 sm:mt-5 sm:text-base sm:leading-7 md:text-[1.12rem]'>
                    Browse recent posts, open each discussion, and stay close to
                    the DocMoonlight community across radiology, infusion, and
                    resident coverage topics.
                  </p>
                </div>

                <button
                  type='button'
                  onClick={openCreatePost}
                  className='inline-flex h-11 items-center justify-center gap-2 rounded-[1rem] bg-[linear-gradient(90deg,#1e2947_0%,#0b0f18_100%)] px-5 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(15,23,42,0.2)] transition hover:translate-y-[-1px] hover:shadow-[0_24px_48px_rgba(15,23,42,0.26)] sm:h-12 sm:gap-3 sm:rounded-[1.1rem] sm:px-6 sm:text-base'
                >
                  <svg
                    aria-hidden='true'
                    viewBox='0 0 24 24'
                    className='h-5 w-5 sm:h-6 sm:w-6'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M12 20h9' />
                    <path d='M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z' />
                  </svg>
                  Create post
                </button>
              </div>
            </div>
          </div>

          <div className='mt-10 space-y-6'>
            <div className='flex flex-wrap items-center gap-3'>
              <button
                type='button'
                onClick={openAllPosts}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition sm:gap-3 sm:px-4.5 sm:py-2.5 sm:text-base ${
                  view === 'all'
                    ? 'bg-[linear-gradient(90deg,#1e2947_0%,#0b0f18_100%)] text-white shadow-[0_12px_28px_rgba(15,23,42,0.18)]'
                    : 'border border-slate-200 bg-slate-50 text-slate-700 shadow-[0_8px_22px_rgba(15,23,42,0.08)] hover:bg-slate-100'
                }`}
              >
                <svg
                  aria-hidden='true'
                  viewBox='0 0 24 24'
                  className='h-4 w-4 sm:h-5 sm:w-5'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M8 6h13' />
                  <path d='M8 12h13' />
                  <path d='M8 18h13' />
                  <path d='M3 6h.01' />
                  <path d='M3 12h.01' />
                  <path d='M3 18h.01' />
                </svg>
                All posts
              </button>
              {session?.id && (
                <button
                  type='button'
                  onClick={openMyPosts}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition sm:gap-3 sm:px-4.5 sm:py-2.5 sm:text-base ${
                    view === 'mine'
                      ? 'bg-[linear-gradient(90deg,#1e2947_0%,#0b0f18_100%)] text-white shadow-[0_12px_28px_rgba(15,23,42,0.18)]'
                      : 'border border-slate-200 bg-slate-50 text-slate-700 shadow-[0_8px_22px_rgba(15,23,42,0.08)] hover:bg-slate-100'
                  }`}
                >
                  <svg
                    aria-hidden='true'
                    viewBox='0 0 24 24'
                    className='h-4 w-4 sm:h-5 sm:w-5'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M20 21a8 8 0 0 0-16 0' />
                    <circle cx='12' cy='7' r='4' />
                  </svg>
                  My posts
                </button>
              )}
            </div>

            {pageError && (
              <div className='rounded-[1.5rem] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700'>
                {pageError}
              </div>
            )}

            {loadingMyPosts && (
              <div className='rounded-[1.5rem] border border-slate-200 bg-white px-5 py-8 text-sm text-slate-600 shadow-[0_18px_45px_rgba(15,23,42,0.08)] sm:rounded-[2rem] sm:px-6 sm:py-10 sm:text-base'>
                Loading your posts...
              </div>
            )}

            {!pageError && !loadingMyPosts && posts.length === 0 && (
              <div className='rounded-[1.5rem] border border-slate-200 bg-white px-5 py-8 text-sm text-slate-600 shadow-[0_18px_45px_rgba(15,23,42,0.08)] sm:rounded-[2rem] sm:px-6 sm:py-10 sm:text-base'>
                {view === 'mine' ? 'You have no posts yet.' : 'No posts yet.'}
              </div>
            )}

            {paginatedPosts.map((post) => (
              <Link
                key={post.slug}
                href={`/forum/${post.slug}`}
                className='group block rounded-[1.5rem] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-4 shadow-[0_16px_45px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:border-[#f5d6a2] hover:shadow-[0_24px_60px_rgba(15,23,42,0.12)] sm:rounded-[2rem] sm:p-6 md:px-8 md:py-7'
              >
                <article className='flex items-start gap-3 sm:gap-5'>
                  <ForumAvatar name={post.author} size='lg' />

                  <div className='min-w-0 flex-1'>
                    <div className='flex flex-wrap items-center gap-2 text-xs text-slate-500 sm:gap-4 sm:text-sm'>
                      <span className='font-medium text-[#12315f]'>
                        {post.author}
                      </span>
                      <span className='h-1.5 w-1.5 rounded-full bg-slate-300' />
                      <span>{post.date}</span>
                    </div>
                    <h2 className='mt-2 text-sm font-black tracking-[-0.01em] text-slate-950 transition group-hover:text-[#12315f] sm:mt-3 sm:text-lg sm:tracking-[-0.03em] md:text-[1.6rem] md:leading-[1.1]'>
                      {post.title}
                    </h2>
                  </div>
                </article>
              </Link>
            ))}

            {!pageError && !loadingMyPosts && posts.length > POSTS_PER_PAGE && (
              <div className='flex flex-wrap items-center justify-between gap-3 rounded-[1.5rem] border border-slate-200 bg-white px-5 py-4 shadow-[0_14px_36px_rgba(15,23,42,0.08)]'>
                <div className='text-xs text-slate-600 sm:text-sm'>
                  Page {safeCurrentPage} of {totalPages}
                </div>
                <div className='flex flex-wrap items-center gap-2'>
                  <button
                    type='button'
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={safeCurrentPage === 1}
                    className='rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-[#f5d6a2] hover:bg-[#fff7eb] hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm'
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1)
                    .slice(
                      Math.max(0, safeCurrentPage - 3),
                      Math.max(0, safeCurrentPage - 3) + 5,
                    )
                    .map((page) => (
                      <button
                        key={page}
                        type='button'
                        onClick={() => setCurrentPage(page)}
                        className={`h-9 w-9 rounded-full text-xs font-bold transition sm:h-10 sm:w-10 sm:text-sm ${
                          safeCurrentPage === page
                            ? 'bg-[linear-gradient(90deg,#1e2947_0%,#0b0f18_100%)] text-white'
                            : 'border border-slate-200 bg-white text-slate-700 hover:border-[#f5d6a2] hover:bg-[#fff7eb] hover:text-slate-950'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  <button
                    type='button'
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={safeCurrentPage === totalPages}
                    className='rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-[#f5d6a2] hover:bg-[#fff7eb] hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:text-sm'
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className='px-4 pb-14 sm:px-6 lg:px-8'>
        <div className='mx-auto max-w-6xl rounded-[2rem] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] px-6 py-8 shadow-[0_18px_50px_rgba(15,23,42,0.08)] md:px-8 md:py-9'>
          <div className='flex flex-col gap-6 md:flex-row md:items-center md:justify-between'>
            <div className='max-w-2xl'>
              <div className='text-xs font-black uppercase tracking-[0.18em] text-[#d18a00] sm:text-sm sm:tracking-[0.2em]'>
                Download Our App
              </div>
              <h2 className='mt-3 text-xl font-black tracking-[-0.03em] text-slate-950 sm:text-2xl sm:tracking-[-0.04em] md:text-[2rem]'>
                Stay connected to the DocMoonlight community.
              </h2>
              <p className='mt-3 text-sm leading-6 text-slate-600 sm:text-base sm:leading-7'>
                Access DocMoonlight on mobile to stay close to updates,
                opportunities, and the broader platform experience.
              </p>
            </div>

            <div className='flex flex-wrap items-center gap-3 md:justify-end'>
              <a
                target='_blank'
                href='https://play.google.com/store/apps/details?id=com.docmoonlight'
                rel='noreferrer'
                className='transition hover:opacity-95'
              >
                <Image
                  src='/images/google_play_btn.svg'
                  alt='Download on Google Play'
                  width={150}
                  height={48}
                  className='h-12 w-[150px] rounded-[10px] bg-white'
                />
              </a>
              <a
                target='_blank'
                href='https://apps.apple.com/us/app/docmoonlight/id6448665833'
                rel='noreferrer'
                className='transition hover:opacity-95'
              >
                <Image
                  src='/images/app_store_btn.svg'
                  alt='Download on the App Store'
                  width={150}
                  height={48}
                  className='h-12 w-[150px] rounded-[10px] bg-white'
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ForumAuthModal
        open={authOpen}
        initialMode={authMode}
        onClose={() => {
          setAuthOpen(false);
          setPendingAction(null);
        }}
        onAuthenticated={handleAuthenticated}
      />

      <Modal
        open={postModalOpen}
        onClose={() => setPostModalOpen(false)}
        title='Create post'
      >
        <div className='space-y-4'>
          <label className='block'>
            <span className='mb-2 block text-sm font-semibold text-slate-700'>
              Header
            </span>
            <input
              type='text'
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={POST_TITLE_MAX_LENGTH}
              className='w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-[#f59e0b] focus:bg-white'
              placeholder='Write a short post header'
            />
          </label>

          <label className='block'>
            <span className='mb-2 block text-sm font-semibold text-slate-700'>
              Text
            </span>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={POST_BODY_MAX_LENGTH}
              rows={7}
              className='w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-[#f59e0b] focus:bg-white'
              placeholder='Write the full post text here'
            />
          </label>

          {formError && (
            <div className='rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700'>
              {formError}
            </div>
          )}

          <button
            type='button'
            onClick={submitPost}
            disabled={!title.trim() || !body.trim() || loadingAction}
            className='w-full rounded-2xl bg-[linear-gradient(90deg,#1e2947_0%,#0b0f18_100%)] px-4 py-3 text-sm font-black text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:bg-[#d6d6d6] disabled:text-[#202020]'
          >
            {loadingAction ? 'Publishing post...' : 'Publish post'}
          </button>
        </div>
      </Modal>
    </SiteLayout>
  );
};

export default ForumPage;
